import React, { useState, useEffect, useId } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import styled from 'styled-components';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';

import ArrowHorizontal from '../img/arrow-horizontal.svg';

import ListingCardUncontained from './ListingCardUncontained';

import {
  PROPY_SPACE_GREY_LIGHT,
  PROPY_BLACK,
} from '../utils';

const ZoneContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding-left: ${props => props.theme.spacing(4)};
  padding-right: ${props => props.theme.spacing(4)};
`;

const ZoneInnerContainer = styled.div`
  max-width: 1280px;
  width: 100%;
  min-height: ${props => props.mobile ? '200px' : '740px'};
`

const TypographyContainer = styled.div`
  text-align: ${props => props.mobile ? 'center' : 'left'};
  max-width: ${props => props.mobile ? '300px' : '1280px'};
  margin-bottom: ${props => props.mobile ? props.theme.spacing(4) : props.theme.spacing(8)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${props => props.mobile ? 'center' : 'start'};
  width: 100%;
`

const TypographySpacer = styled.div`
  margin-top: ${props => props.theme.spacing(1.5)};
`

const MobileSlideContainer = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 500px;
  width: calc(100% - 32px);
  margin-left: auto;
  margin-right: auto;
`

const InnerSlideContainer = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-left: ${props => props.theme.spacing(2)};
  padding-right: ${props => props.theme.spacing(2)};
`

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: end;
`

const animationVariants = {
  enter: {
    x: 50,
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exit: {
    x: -50,
    opacity: 0
  }
};

const FeaturedListings = (props) => {

    const {
      btcPrice,
      setBtcPrice,
      isConsideredMobile,
    } = props;

    const [listings, setListings] = useState([]);
    // const [flagshipListing, setFlagshipListing] = useState(null);
    const [activeListingIndex, setActiveListingIndex] = useState(0);

    const componentId = useId();

    useEffect(() => {
      let isMounted = true;
      fetch("https://api.propy.com/api/v3/properties/premium")
        .then(resp => resp.json())
        .then(async (response) => {
          let rwaProperty = await fetch("https://api.propy.com/api/Properties/single/67703c37ed6af3ec687a3d1e").then(resp => resp.json()).then(result => result?.data);
          rwaProperty.chips = [
            {
              label: "RWA SALE",
              textColor: "#333333",
              backgroundColor: "#68FF72",
            }
          ];
          let curatedListings = [];
          let listingIds = new Set(); // Using Set for more efficient lookups
          const totalLimit = 6;

          // Helper function to process entries
          const processEntry = (entry) => {
            if (listingIds.has(entry.id)) return null;
            
            entry.imageUrls = entry.imageUrls.map(
              (item) => `${item}?width=680&height=auto&scale=both&mode=crop`
            );
            entry.accepts_crypto = true;
            
            listingIds.add(entry.id);
            return entry;
          };

          // Combine all lines into a single array
          const allEntries = [
            ...response.line1,
            ...response.line2,
            ...response.line3
          ];

          // First, add featured properties while maintaining their original order
          const featuredProperties = allEntries
            .filter(entry => entry.isFeaturedProperty)
            .map(processEntry)
            .filter(entry => entry !== null && !entry.isExpired);

          // Get remaining non-featured properties
          const remainingProperties = allEntries
            .filter(entry => !entry.isFeaturedProperty)
            .map(processEntry)
            .filter(entry => entry !== null && !entry.isExpired);

          // Shuffle remaining properties
          const shuffleArray = (array) => {
            for (let i = array.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [array[i], array[j]] = [array[j], array[i]];
            }
            return array;
          };

          // Combine featured and shuffled remaining properties
          curatedListings = [
            rwaProperty,
            ...featuredProperties,
            ...shuffleArray(remainingProperties)
          ].slice(0, totalLimit);
          
          if (isMounted) {
            setListings(curatedListings);
            // setFlagshipListing(rwaProperty);
          }
        })
      fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd")
      .then(resp => resp.json())
      .then(response => {
        if(response?.bitcoin?.usd && (response?.bitcoin?.usd > 0) && isMounted && setBtcPrice) {
          setBtcPrice(response?.bitcoin?.usd);
        }
      })
      .catch((error) => {
        console.log({error})
      })
      return () => {
        isMounted = false;
      };
    }, [setBtcPrice])

    const handleNext = () => {
      if (activeListingIndex < listings.length) {
        setActiveListingIndex(prev => prev + 1);
      }
    };

    const handlePrev = () => {
      if (activeListingIndex > 0) {
        setActiveListingIndex(prev => prev - 1);
      }
    };

    const getMdWidth = (index) => {
      if(index === 0) {
        return 8;
      }
      if(index === 5) {
        return 12;
      }
      return 4;
    }

    return (
      <ZoneContainer>
        <TypographyContainer mobile={isConsideredMobile}>
          <Typography
            sx={{ fontFamily: fonts.spaceGrotesk, textTransform: 'uppercase', color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', ...(isConsideredMobile && { fontSize: '14px', lineHeight: '14px' }) }}
          >
            Grant Cardone lists with Propy
          </Typography>
          <TypographySpacer>
            <Typography 
              variant="h4"
              sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', maxWidth: '890px', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}
            >
              AI lead gen tools and crypto buyers get you max exposure.
            </Typography>
          </TypographySpacer>
        </TypographyContainer>
        <ZoneInnerContainer mobile={isConsideredMobile}>
          {!isConsideredMobile &&
            <Grid container spacing={2}>
              {/* {flagshipListing &&
                <Grid key={`featured-flagship-listing-${componentId}`} item xs={12} sm={12} md={12} lg={12}>
                  <div>
                    <ListingCardUncontained
                      listing={flagshipListing}
                      btcPrice={btcPrice}
                    />
                  </div>
                </Grid>
              } */}
              {listings.map(
                (listing, index) => {
                  return (
                    <Grid key={`featured-listing-${index}-${componentId}`} item xs={12} sm={12} md={getMdWidth(index)} lg={(index === 0 || index === 5) ? 6 : 3}>
                      <div>
                        <ListingCardUncontained
                          listing={listing}
                          btcPrice={btcPrice}
                        />
                      </div>
                    </Grid>
                  )
                }
              )}
            </Grid>
          }
          {isConsideredMobile &&
            <MobileSlideContainer>
              <InnerSlideContainer>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`featured-listing-${activeListingIndex}-step-number`}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    variants={animationVariants}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}
                  >
                    <ListingCardUncontained
                      isConsideredMobile={isConsideredMobile}
                      listing={listings[activeListingIndex]}
                      btcPrice={btcPrice}
                    />
                  </motion.div>
                </AnimatePresence>
              </InnerSlideContainer>
              <Controls>
                <IconButton
                  onClick={handlePrev}
                  disabled={activeListingIndex === 0}
                  sx={{
                    border: '1px solid #38A6FB',
                    height: '44px',
                    width: '44px',
                    marginRight: '8px',
                    opacity: activeListingIndex === 0 ? 0.5 : 1,
                  }} 
                  aria-label="backwards"
                >
                  <Box
                    component={ArrowHorizontal}
                    sx={{
                      width: '24px',
                      height: '24px',
                      color: 'primary.main',
                      '& path': { fill: '#38A6FB' }
                    }}
                  />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  disabled={activeListingIndex >= (listings.length - 1)}
                  sx={{
                    border: '1px solid #38A6FB',
                    height: '44px',
                    width: '44px',
                    opacity: activeListingIndex >= (listings.length - 1) ? 0.5 : 1,
                  }}
                  aria-label="forwards"
                >
                  <Box
                    component={ArrowHorizontal}
                    sx={{
                      width: 24,
                      height: 24,
                      transform: 'rotate(180deg)',
                      color: 'primary.main',
                      '& path': { fill: '#38A6FB' }
                    }}
                  />
                </IconButton>
              </Controls>
            </MobileSlideContainer>
          }
        </ZoneInnerContainer>
      </ZoneContainer>
    )
};

export default FeaturedListings;